import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
    GetContractDealsByIdsInput,
    GetContractDealsByIdsOutput,
    GetRegionsByIdInput,
    GetTerritoriesByIdInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import Toggle from '@amzn/meridian/toggle';

import DatePicker from 'react-datepicker';
import { contractConfigurationClient } from 'src/api/client';
import { dateToString, stringToDate } from 'src/components/MonthRangePicker/utils';
import { TerritoryFlag } from 'src/components/TerritoryFlag';
import { AppContext } from 'src/contexts/appContext';
import 'src/styles/react-datepicker.css';
import { IRegion } from 'src/types/IOptions';

interface CreateRoyaltyManagerRequestModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (requestData: any) => void;
    initialData?: any;
    isUpdate?: boolean;
}

export const CreateRoyaltyManagerRequestModal: React.FC<CreateRoyaltyManagerRequestModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    initialData,
    isUpdate = false
}) => {
    const { userInfo, setTopAlertMsg, metadata } = useContext(AppContext);

    const [formData, setFormData] = useState(() => ({
        requestDate: new Date().toISOString(),
        requestedBy: userInfo.username || '',
        description: '',
        periodFrom: new Date().toISOString().slice(0, 7).replace('-', ''), // YYYYMM format
        periodTo: new Date().toISOString().slice(0, 7).replace('-', ''), // YYYYMM format
        priority: 1,
        isAllServices: true,
        serviceNames: [] as string[],
        isAllRegions: true,
        regionCodes: [] as string[],
        territoryCodes: [] as string[],
        isAllEntities: true,
        entityNames: [] as string[]
    }));

    const [services, setServices] = useState<any[]>([]);
    const [regions, setRegions] = useState<IRegion[]>([]);
    const [territories, setTerritories] = useState<any[]>([]);
    const [entities, setEntities] = useState<any[]>([]);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});

    // Convert YYYYMM format to Date objects for DatePicker
    const periodFromDate = useMemo(() => {
        if (!formData.periodFrom || formData.periodFrom.length !== 6) return null;
        const year = formData.periodFrom.slice(0, 4);
        const month = formData.periodFrom.slice(4, 6);
        return stringToDate(`${year}-${month}`);
    }, [formData.periodFrom]);

    const periodToDate = useMemo(() => {
        if (!formData.periodTo || formData.periodTo.length !== 6) return null;
        const year = formData.periodTo.slice(0, 4);
        const month = formData.periodTo.slice(4, 6);
        return stringToDate(`${year}-${month}`);
    }, [formData.periodTo]);

    useEffect(() => {
        if (isOpen) {
            if (isUpdate && initialData) {
                setFormData({
                    requestDate: initialData.requestDate || new Date().toISOString(),
                    requestedBy: initialData.requestedBy || userInfo.username || '',
                    description: initialData.description || '',
                    periodFrom: initialData.periodFrom || '',
                    periodTo: initialData.periodTo || '',
                    priority: initialData.priority || 1,
                    isAllServices: initialData.isAllServices ?? true,
                    serviceNames: initialData.serviceNames || [],
                    isAllRegions: initialData.isAllRegions ?? true,
                    regionCodes: initialData.regionCodes || [],
                    territoryCodes: initialData.territoryCodes || [],
                    isAllEntities: initialData.isAllEntities ?? true,
                    entityNames: initialData.entityNames || []
                });
            } else {
                const newFormData = {
                    requestDate: new Date().toISOString(),
                    requestedBy: userInfo.username || '',
                    description: '',
                    periodFrom: new Date().toISOString().slice(0, 7).replace('-', ''),
                    periodTo: new Date().toISOString().slice(0, 7).replace('-', ''),
                    priority: 1,
                    isAllServices: true,
                    serviceNames: [] as string[],
                    isAllRegions: true,
                    regionCodes: [] as string[],
                    territoryCodes: [] as string[],
                    isAllEntities: true,
                    entityNames: [] as string[]
                };
                setFormData(newFormData);
            }
        }
    }, [isOpen, userInfo.username, isUpdate, initialData]);
    useEffect(() => {
        if (isOpen && !isLoadingData) {
            setIsLoadingData(true);

            const loadData = async () => {
                try {
                    // Use services from metadata (already loaded in AppContext)
                    if (metadata?.serviceMapping) {
                        const servicesArray = Object.values(metadata.serviceMapping).filter(
                            (service) => service.serviceId !== 0
                        ); // Exclude "All Services"
                        setServices(servicesArray);
                    }

                    // Load regions and territories via API, then filter regions based on launched territories
                    const regionsPromise = contractConfigurationClient
                        .getRegionsById(GetRegionsByIdInput.clone({}))
                        .toPromise();
                    const territoriesPromise = contractConfigurationClient
                        .getTerritoriesById(GetTerritoriesByIdInput.clone({}))
                        .toPromise();

                    Promise.all([regionsPromise, territoriesPromise]).then(
                        ([regionsResponse, territoriesResponse]) => {
                            // Get all launched territories
                            const launchedTerritories = (territoriesResponse.territories || [])
                                .filter(
                                    (territory) =>
                                        territory.territoryId && territory.territoryCode && territory.territoryName
                                )
                                .map((territory) => ({
                                    territoryId: territory.territoryId,
                                    territoryCode: territory.territoryCode,
                                    territoryName: territory.territoryName
                                }));
                            setTerritories(launchedTerritories);

                            // Filter regions to only include those that have launched territories
                            if (metadata?.territoryMapping) {
                                const launchedRegionIds = new Set<number>();

                                // Find region IDs that contain launched territories
                                launchedTerritories.forEach((territory) => {
                                    const territoryInfo = metadata.territoryMapping[territory.territoryId!];
                                    if (territoryInfo?.regionId) {
                                        launchedRegionIds.add(territoryInfo.regionId);
                                    }
                                });

                                // Filter regions to only include those with launched territories
                                const regionsArray = (regionsResponse.regions || []).filter(
                                    (region) =>
                                        region.regionId &&
                                        region.regionCode &&
                                        region.regionName &&
                                        launchedRegionIds.has(region.regionId)
                                ) as IRegion[];
                                setRegions(regionsArray);
                            }
                        },
                        (error: UnknownCoralError) => {
                            console.error('Error loading regions and territories:', error);
                            setRegions([]);
                            setTerritories([]);
                        }
                    );

                    // Load entities from contract deals (same pattern as SelectVendor component)
                    contractConfigurationClient
                        .getContractDealsByIds(
                            GetContractDealsByIdsInput.clone({
                                includeDeletedContracts: false,
                                includeDraftContracts: false,
                                includeDeletedContractDeals: false,
                                includeIneligibleContractDeals: true
                            })
                        )
                        .subscribe(
                            (response: GetContractDealsByIdsOutput) => {
                                const entitiesMap = new Map();

                                response.contractDeals?.forEach((contract) => {
                                    contract.entityActiveIntervals?.forEach((entityInterval) => {
                                        if (entityInterval.entityId && entityInterval.entityName) {
                                            entitiesMap.set(entityInterval.entityId, {
                                                entityId: entityInterval.entityId,
                                                entityName: entityInterval.entityName
                                            });
                                        }
                                    });
                                });

                                const uniqueEntities = Array.from(entitiesMap.values());
                                setEntities(uniqueEntities);
                            },
                            (error: UnknownCoralError) => {
                                setEntities([]);
                            }
                        );
                } catch (error) {
                    console.error('Error loading services and territories:', error);
                } finally {
                    setIsLoadingData(false);
                }
            };

            loadData();
        }
    }, [isOpen, metadata]);

    const validatePeriodRange = (periodFrom: string, periodTo: string) => {
        if (formData.isAllEntities) return;
        // Check range validation: periodTo must be >= periodFrom
        if (periodFrom && periodTo) {
            const fromYear = parseInt(periodFrom.substring(0, 4));
            const fromMonth = parseInt(periodFrom.substring(4, 6));
            const toYear = parseInt(periodTo.substring(0, 4));
            const toMonth = parseInt(periodTo.substring(4, 6));

            const periodRangeValid = fromYear < toYear || (fromYear === toYear && fromMonth <= toMonth);
            if (!periodRangeValid) {
                setErrorMessages((prev) => ({
                    ...prev,
                    periodTo: 'Period To must be greater than or equal to Period From'
                }));
            } else {
                setErrorMessages((prev) => {
                    const newState = { ...prev };
                    delete newState.periodTo;
                    return newState;
                });
            }
        }
    };

    const handlePeriodFromChange = (date: Date | null) => {
        if (date) {
            const monthStr = dateToString(date); // Returns "YYYY-MM"
            const yyyymm = monthStr.replace('-', ''); // Convert to "YYYYMM"
            handleInputChange('periodFrom', yyyymm);
            validatePeriodRange(yyyymm, formData.periodTo);
        }
    };

    const handlePeriodToChange = (date: Date | null) => {
        if (date) {
            const monthStr = dateToString(date);
            const yyyymm = monthStr.replace('-', '');
            handleInputChange('periodTo', yyyymm);
            validatePeriodRange(formData.periodFrom, yyyymm);
        }
    };

    const handleInputChange = (field: string, value: any) => {
        setFormData((prev) => {
            const newFormData = {
                ...prev,
                [field]: value
            };

            // When "All Entities" is selected, restrict to single month
            if (field === 'isAllEntities' && value === true) {
                // Set Period To to match Period From when All Entities is enabled
                newFormData.periodTo = newFormData.periodFrom;
            } else if (field === 'periodFrom' && prev.isAllEntities) {
                // When All Entities is selected and Period From changes, update Period To to match
                newFormData.periodTo = value;
            }

            // When a region is selected/deselected, automatically select/deselect corresponding territories
            if (field === 'regionCodes' && metadata?.territoryMapping) {
                const selectedRegionCodes = value as string[];
                const previousRegionCodes = prev.regionCodes || [];

                // Find newly added regions
                const addedRegions = selectedRegionCodes.filter((code) => !previousRegionCodes.includes(code));
                // Find removed regions
                const removedRegions = previousRegionCodes.filter((code) => !selectedRegionCodes.includes(code));

                let updatedTerritories = [...(prev.territoryCodes || [])];

                // Add territories for newly selected regions
                if (addedRegions.length > 0) {
                    const addedRegionIds = regions
                        .filter((region) => addedRegions.includes(region.regionCode!))
                        .map((region) => region.regionId);

                    const territoriesToAdd: string[] = [];
                    territories.forEach((territory) => {
                        const territoryInfo = metadata.territoryMapping[territory.territoryId];
                        if (territoryInfo?.regionId && addedRegionIds.includes(territoryInfo.regionId)) {
                            if (!territoriesToAdd.includes(territory.territoryCode)) {
                                territoriesToAdd.push(territory.territoryCode);
                            }
                        }
                    });

                    // Add new territories to existing selection
                    updatedTerritories = [...updatedTerritories, ...territoriesToAdd];
                }

                // Remove territories for deselected regions
                if (removedRegions.length > 0) {
                    const removedRegionIds = regions
                        .filter((region) => removedRegions.includes(region.regionCode!))
                        .map((region) => region.regionId);

                    const territoriesToRemove: string[] = [];
                    territories.forEach((territory) => {
                        const territoryInfo = metadata.territoryMapping[territory.territoryId];
                        if (territoryInfo?.regionId && removedRegionIds.includes(territoryInfo.regionId)) {
                            territoriesToRemove.push(territory.territoryCode);
                        }
                    });

                    // Remove territories that belong to deselected regions
                    updatedTerritories = updatedTerritories.filter((code) => !territoriesToRemove.includes(code));
                }

                // Remove duplicates and update form data
                newFormData.territoryCodes = Array.from(new Set(updatedTerritories));
            }

            return newFormData;
        });
    };

    const handleSubmit = () => {
        const requestData = {
            requestDate: formData.requestDate,
            requestedBy: formData.requestedBy,
            description: formData.description,
            periodFrom: formData.periodFrom,
            periodTo: formData.periodTo,
            priority: formData.priority,
            isAllServices: formData.isAllServices,
            serviceNames: formData.isAllServices ? undefined : formData.serviceNames,
            isAllRegions: formData.isAllRegions,
            regionCodes: formData.isAllRegions ? undefined : formData.regionCodes,
            territoryCodes: formData.territoryCodes.length > 0 ? formData.territoryCodes : undefined,
            isAllEntities: formData.isAllEntities,
            entityNames: formData.isAllEntities ? undefined : formData.entityNames,
            isMonthEnd: false // Always pass false to API
        };

        onSubmit(requestData);
        onClose();

        // Reset form
        setFormData({
            requestDate: new Date().toISOString(),
            requestedBy: userInfo.username || '',
            description: '',
            periodFrom: new Date().toISOString().slice(0, 7).replace('-', ''),
            periodTo: new Date().toISOString().slice(0, 7).replace('-', ''),
            priority: 1,
            isAllServices: true,
            serviceNames: [],
            isAllRegions: true,
            regionCodes: [],
            territoryCodes: [],
            isAllEntities: true,
            entityNames: []
        });
    };

    const isFormValid = () => {
        const basicFieldsValid =
            formData.requestedBy.trim() &&
            formData.description.trim() &&
            formData.periodFrom.trim() &&
            formData.periodTo.trim();

        // Additional validation for services
        const servicesValid = formData.isAllServices || formData.serviceNames.length > 0;

        // Additional validation for regions - when toggle is off, need either region codes OR territory codes
        const regionsValid =
            formData.isAllRegions || formData.regionCodes.length > 0 || formData.territoryCodes.length > 0;

        // Additional validation for entities
        const entitiesValid = formData.isAllEntities || formData.entityNames.length > 0;

        // Check if there are any error messages
        const hasErrors = Object.keys(errorMessages).length > 0;

        return basicFieldsValid && servicesValid && regionsValid && entitiesValid && !hasErrors;
    };

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            title={
                isUpdate ? 'Update Royalty Manager Execution Request' : 'Create New Royalty Manager Execution Request'
            }
        >
            <div className="create-royalty-request-modal-content">
                <Column spacing="500">
                    {/* === BASIC INFORMATION SECTION === */}
                    <Column spacing="300">
                        <Text type="h400">Basic Information</Text>

                        {/* Requested By */}
                        <Column spacing="200">
                            <Text type="b300">Requested By</Text>
                            <Input
                                value={formData.requestedBy}
                                disabled={true}
                                placeholder="Auto-populated from user"
                            />
                        </Column>

                        {/* Period Range */}
                        <Column>
                            <Row spacing="400">
                                <Column spacing="200">
                                    <Text type="b300">Period From *</Text>
                                    <DatePicker
                                        className="month-filter-input"
                                        selected={periodFromDate}
                                        onChange={handlePeriodFromChange}
                                        dateFormat="yyyy-MM"
                                        showMonthYearPicker
                                        placeholderText="Select Period From"
                                    />
                                </Column>
                                <Column spacing="200">
                                    <Text type="b300">Period To *</Text>
                                    <DatePicker
                                        className="month-filter-input"
                                        selected={periodToDate}
                                        onChange={handlePeriodToChange}
                                        dateFormat="yyyy-MM"
                                        showMonthYearPicker
                                        placeholderText="Select Period To"
                                        disabled={formData.isAllEntities}
                                    />
                                </Column>
                            </Row>
                            <Row spacing="200">
                                {formData.isAllEntities && (
                                    <Text type="b100">
                                        When "All Entities" is selected, only single month processing is allowed
                                    </Text>
                                )}
                                {errorMessages.periodTo && (
                                    <Text type="b100" color="error">
                                        {errorMessages.periodTo}
                                    </Text>
                                )}
                            </Row>
                        </Column>

                        {/* Description */}
                        <Column spacing="200">
                            <Text type="b300">Description *</Text>
                            <Textarea
                                value={formData.description}
                                onChange={(value: string) => handleInputChange('description', value)}
                                placeholder="Describe the royalty calculation request..."
                                resize="none"
                                width="100%"
                                rows={3}
                            />
                        </Column>
                    </Column>

                    {/* === SCOPE CONFIGURATION SECTION === */}
                    <Column spacing="300">
                        <Text type="h400">Scope Configuration</Text>

                        {/* Services Section */}
                        <Column spacing="200">
                            <Row alignmentVertical="center" spacing="200">
                                <Text type="b300">Services</Text>
                                <Toggle
                                    checked={formData.isAllServices}
                                    onChange={(checked) => handleInputChange('isAllServices', checked)}
                                />
                                <Text type="b200">All Services</Text>
                            </Row>
                            {!formData.isAllServices && (
                                <Row spacing="300" alignmentVertical="top">
                                    <Select
                                        value=""
                                        onChange={(value) => {
                                            if (value && !formData.serviceNames.includes(value)) {
                                                handleInputChange('serviceNames', [...formData.serviceNames, value]);
                                            }
                                        }}
                                        placeholder="Select services"
                                        width="400px"
                                    >
                                        {services.map((service) => (
                                            <SelectOption
                                                key={service.serviceId}
                                                value={service.serviceName}
                                                label={service.serviceName}
                                            />
                                        ))}
                                    </Select>
                                    {formData.serviceNames.length > 0 && (
                                        <Row spacing="100">
                                            {formData.serviceNames.map((service, index) => (
                                                <Box key={index} type="outline" spacingInset="100">
                                                    <Row spacing="100" alignmentVertical="center">
                                                        <Text type="b200">{service}</Text>
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            onClick={() =>
                                                                handleInputChange(
                                                                    'serviceNames',
                                                                    formData.serviceNames.filter((s) => s !== service)
                                                                )
                                                            }
                                                        >
                                                            ×
                                                        </Button>
                                                    </Row>
                                                </Box>
                                            ))}
                                        </Row>
                                    )}
                                </Row>
                            )}
                        </Column>

                        {/* Regions Section */}
                        <Column spacing="200">
                            <Row alignmentVertical="center" spacing="200">
                                <Text type="b300">Regions</Text>
                                <Toggle
                                    checked={formData.isAllRegions}
                                    onChange={(checked) => handleInputChange('isAllRegions', checked)}
                                />
                                <Text type="b200">All Regions</Text>
                            </Row>
                            {!formData.isAllRegions && (
                                <Row spacing="300" alignmentVertical="top">
                                    <Select
                                        value=""
                                        onChange={(value) => {
                                            if (value && !formData.regionCodes.includes(value)) {
                                                handleInputChange('regionCodes', [...formData.regionCodes, value]);
                                            }
                                        }}
                                        placeholder="Select regions"
                                        width="400px"
                                    >
                                        {regions.map((region) => (
                                            <SelectOption
                                                key={region.regionId}
                                                value={region.regionCode}
                                                label={`${region.regionCode} - ${region.regionName}`}
                                            />
                                        ))}
                                    </Select>
                                    {formData.regionCodes.length > 0 && (
                                        <Row spacing="100">
                                            {formData.regionCodes.map((region, index) => (
                                                <Box key={index} type="outline" spacingInset="100">
                                                    <Row spacing="100" alignmentVertical="center">
                                                        <Text type="b200">{region}</Text>
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            onClick={() =>
                                                                handleInputChange(
                                                                    'regionCodes',
                                                                    formData.regionCodes.filter((r) => r !== region)
                                                                )
                                                            }
                                                        >
                                                            ×
                                                        </Button>
                                                    </Row>
                                                </Box>
                                            ))}
                                        </Row>
                                    )}
                                </Row>
                            )}
                        </Column>

                        {/* Territory Codes Section */}
                        <Column spacing="200">
                            <Text type="b300">Territory Codes (Optional)</Text>
                            <Row spacing="300" alignmentVertical="top">
                                <Select
                                    value=""
                                    onChange={(value) => {
                                        if (value && !formData.territoryCodes.includes(value)) {
                                            handleInputChange('territoryCodes', [...formData.territoryCodes, value]);
                                        }
                                    }}
                                    placeholder="Select territories"
                                    width="400px"
                                >
                                    {territories.map((territory) => (
                                        <SelectOption
                                            key={territory.territoryId}
                                            value={territory.territoryCode}
                                            label={territory.territoryCode}
                                        >
                                            {() => <TerritoryFlag territoryCode={territory.territoryCode} />}
                                        </SelectOption>
                                    ))}
                                </Select>
                                {formData.territoryCodes.length > 0 && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '8px',
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        {formData.territoryCodes.map((territory, index) => (
                                            <Box key={index} type="outline" spacingInset="100">
                                                <Row spacing="100" alignmentVertical="center">
                                                    <Text type="b200">{territory}</Text>
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        onClick={() =>
                                                            handleInputChange(
                                                                'territoryCodes',
                                                                formData.territoryCodes.filter((t) => t !== territory)
                                                            )
                                                        }
                                                    >
                                                        ×
                                                    </Button>
                                                </Row>
                                            </Box>
                                        ))}
                                    </div>
                                )}
                            </Row>
                        </Column>

                        {/* Entities Section */}
                        <Column spacing="200">
                            <Row alignmentVertical="center" spacing="200">
                                <Text type="b300">Entities</Text>
                                <Toggle
                                    checked={formData.isAllEntities}
                                    onChange={(checked) => handleInputChange('isAllEntities', checked)}
                                />
                                <Text type="b200">All Entities</Text>
                            </Row>
                            {!formData.isAllEntities && (
                                <Row spacing="300" alignmentVertical="top">
                                    <Select
                                        value=""
                                        onChange={(value) => {
                                            if (value && !formData.entityNames.includes(value)) {
                                                handleInputChange('entityNames', [...formData.entityNames, value]);
                                            }
                                        }}
                                        placeholder="Select entities"
                                        width="400px"
                                    >
                                        {entities.map((entity) => (
                                            <SelectOption
                                                key={entity.entityId}
                                                value={entity.entityName}
                                                label={entity.entityName}
                                            />
                                        ))}
                                    </Select>
                                    {formData.entityNames.length > 0 && (
                                        <Row spacing="100">
                                            {formData.entityNames.map((entity, index) => (
                                                <Box key={index} type="outline" spacingInset="100">
                                                    <Row spacing="100" alignmentVertical="center">
                                                        <Text type="b200">{entity}</Text>
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            onClick={() =>
                                                                handleInputChange(
                                                                    'entityNames',
                                                                    formData.entityNames.filter((e) => e !== entity)
                                                                )
                                                            }
                                                        >
                                                            ×
                                                        </Button>
                                                    </Row>
                                                </Box>
                                            ))}
                                        </Row>
                                    )}
                                </Row>
                            )}
                        </Column>
                    </Column>
                </Column>
            </div>

            <ModalFooter>
                <Row alignmentHorizontal="end" spacing="200">
                    <Button type="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="primary" onClick={handleSubmit} disabled={!isFormValid()}>
                        {isUpdate ? 'Update Request' : 'Create Request'}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};
